import {useGetAnalysisListCounts, useGetAnalysisList} from '@domain/rcfa'
import {DashboardWidget, WidgetErrorBoundary} from '@hconnect/common/components'
import {AnalysisListType} from '@hconnect/common/types'
import AddIcon from '@mui/icons-material/Add'
import FactoryIcon from '@mui/icons-material/FactoryOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import LanguageIcon from '@mui/icons-material/Language'
import PermIdentity from '@mui/icons-material/PermIdentity'
import {Button, Grid, GridProps} from '@mui/material'
import {useTranslation} from 'react-i18next'

import {RCFA_OVERVIEW_WIDGET_NAMESPACE} from '../consts'

import {RcfaOverviewCard} from './RcfaOverviewCard'

export interface RcfaOverviewProps {
  plantUpmId: string
  gridProps?: Omit<GridProps, 'item' | 'zeroMinWidth'>
  onNavigateToNew: () => void
  onNavigateToList: (listType: AnalysisListType) => void
}

const defaultGridProps: Omit<GridProps, 'item' | 'zeroMinWidth'> = {xs: 12, sm: true, md: 3}

const RcfaOverviewContent = ({
  plantUpmId,
  gridProps = defaultGridProps,
  onNavigateToNew,
  onNavigateToList
}: RcfaOverviewProps) => {
  const {t} = useTranslation(RCFA_OVERVIEW_WIDGET_NAMESPACE)
  const {data: counts, isLoading: countsLoading} = useGetAnalysisListCounts(plantUpmId)
  const {data: listData} = useGetAnalysisList({
    plantUpmId,
    page: 0,
    rowsNumber: 10,
    listType: AnalysisListType.Mine,
    filters: [],
    order: []
  })

  const showCreateButton = Boolean(listData?.permissions?.canCreateAnalysis)

  return (
    <DashboardWidget
      title={t('title')}
      gridProps={gridProps}
      minWidth={330}
      headerAction={
        showCreateButton && (
          <Button startIcon={<AddIcon />} variant={'text'} onClick={onNavigateToNew}>
            {t('createButton')}
          </Button>
        )
      }
    >
      <Grid container spacing={1.5}>
        <RcfaOverviewCard
          count={counts?.mineCount}
          isLoading={countsLoading}
          Icon={PermIdentity}
          listLabel={t('mineLabel')}
          onClick={() => onNavigateToList(AnalysisListType.Mine)}
        />
        <RcfaOverviewCard
          count={counts?.contributingCount}
          isLoading={countsLoading}
          Icon={GroupsOutlinedIcon}
          listLabel={t('contributingLabel')}
          onClick={() => onNavigateToList(AnalysisListType.Contributing)}
        />
        <RcfaOverviewCard
          count={counts?.myPlantCount}
          isLoading={countsLoading}
          Icon={FactoryIcon}
          listLabel={t('myPlantLabel')}
          onClick={() => onNavigateToList(AnalysisListType.MyPlant)}
        />
        <RcfaOverviewCard
          count={counts?.globalCount}
          isLoading={countsLoading}
          Icon={LanguageIcon}
          listLabel={t('globalLabel')}
          onClick={() => onNavigateToList(AnalysisListType.Global)}
        />
      </Grid>
    </DashboardWidget>
  )
}

export const RcfaOverview = ({gridProps = defaultGridProps, ...props}: RcfaOverviewProps) => (
  <WidgetErrorBoundary gridProps={gridProps}>
    <RcfaOverviewContent gridProps={gridProps} {...props} />
  </WidgetErrorBoundary>
)
